import { Alert, AlertDescription } from "@/components/ui/alert";
import { IconProgressCircle } from "@/components/ui/icons";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetFolderProgress } from "@/features/quiz/api/use-get-folder-progress";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useGetQuizProgress } from "../api/use-get-quiz-progress";

type Props = {
  studySetId?: string;
  folderId?: string;
  userId?: string;
  className?: string;
};

export const StudySetProgressCard = ({
  studySetId,
  folderId,
  userId,
  className,
}: Props) => {
  const t = useI18n();
  const studySetQuery = useGetQuizProgress(studySetId);
  const folderQuery = useGetFolderProgress(folderId);
  const { data: quizProgress, status } = folderId ? folderQuery : studySetQuery;

  if (status === "error") {
    return (
      <div className={cn("bg-[#FAFAFB] border p-6 rounded-2xl", className)}>
        <Alert variant="destructive">
          <AlertDescription>{t("studySetProgress.error")}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (status === "pending") {
    return <StudySetProgressCardSkeleton />;
  }

  // Ensure we have valid numbers for our calculations
  const unfamiliar = Number(quizProgress?.status.unfamiliar) || 0;
  const stillLearning = Number(quizProgress?.status.stillLearning) || 0;
  const familiar = Number(quizProgress?.status.familiar) || 0;
  const mastered = Number(quizProgress?.status.mastered) || 0;
  const total = unfamiliar + stillLearning + familiar + mastered;
  const percentage = quizProgress.averagePercentage ?? 0;

  const statuses = [
    {
      status: (
        <p className="font-medium text-sm text-[#EF0000]">
          {t("studySetProgress.status.unfamiliar")}
        </p>
      ),
      backgroundColor: "bg-[#EF00001A]",
      ring: (
        <IconProgressCircle strokeWidth={4} fill="#EF0000" className="size-4" />
      ),
      count: unfamiliar,
      key: "Unfamiliar",
    },
    {
      status: (
        <p className="font-medium text-sm text-[#FF8000]">
          {t("studySetProgress.status.stillLearning")}
        </p>
      ),
      backgroundColor: "bg-[#FF80001A]",
      ring: (
        <IconProgressCircle strokeWidth={4} fill="#FF8000" className="size-4" />
      ),
      count: stillLearning,
      key: "Still Learning",
    },
    {
      status: (
        <p className="font-medium text-sm text-[#0077FF]">
          {t("studySetProgress.status.familiar")}
        </p>
      ),
      backgroundColor: "bg-[#0077FF1A]",
      ring: (
        <IconProgressCircle strokeWidth={4} fill="#0077FF" className="size-4" />
      ),
      count: familiar,
      key: "Familiar",
    },
    {
      status: (
        <p className="font-medium text-sm text-[#1DAD2C]">
          {t("studySetProgress.status.mastered")}
        </p>
      ),
      backgroundColor: "bg-[#1DAD2C1A]",
      ring: (
        <IconProgressCircle strokeWidth={4} fill="#1DAD2C" className="size-4" />
      ),
      count: mastered,
      key: "Mastered",
    },
  ];

  return (
    <div
      className={cn("bg-[#FAFAFB] border p-6 rounded-2xl w-full", className)}
    >
      <div className="flex flex-col justify-center items-start gap-5">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex w-full items-center justify-between">
            <h2 className="font-semibold text-base text-foreground">
              {t("studySetProgress.title")}
            </h2>
            <p className="text-xs font-bold text-[#0077FF]">
              {percentage}% {t("studySetProgress.completed")}
            </p>
          </div>
          <Progress value={percentage} className="h-2" />
        </div>
        <div className="flex flex-col gap-3 w-full">
          {statuses.map((s) => (
            <div
              key={s.key}
              className="flex flex-row justify-start items-center gap-2 w-full"
            >
              <div
                className={cn(
                  "flex flex-row justify-start items-center gap-2 rounded-lg py-2 px-[14px] min-w-[180px] md:min-w-[210px]",
                  s.backgroundColor,
                )}
                style={{
                  width: `${total > 0 ? (s.count / total) * 100 : 0}%`,
                }}
              >
                {s.ring}
                <span className="text-xs font-medium">{s.status}</span>
              </div>
              <p
                className={cn(
                  "text-sm font-medium border py-2 px-3 rounded-[8px] border-[#0000001A] min-w-[35px] grid place-items-center",
                )}
              >
                {s.count}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const StudySetProgressCardSkeleton = () => {
  return (
    <div className={cn("bg-[#FAFAFB] border p-6 rounded-2xl")}>
      <div className="flex flex-col justify-center items-start gap-5">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex w-full items-center justify-between">
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-2 w-full" />
        </div>
        <div className="flex flex-col gap-3 w-full">
          {["a", "b", "c", "d"].map((id) => (
            <div
              key={`progress-skeleton-${id}`}
              className="flex flex-row items-center gap-2 w-full"
            >
              <Skeleton className="h-10 w-[180px] md:w-[210px] rounded-lg" />
              <Skeleton className="h-10 w-[35px] rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
